import { Component, EventEmitter, Input, OnInit, Output, inject, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { FloatLabelModule } from 'primeng/floatlabel';
import { AgentTriggerComponent } from '../../common-ui-components/agent-trigger/agent-trigger.component';
import { HighlightPipe } from '../../pipes/highlight.pipe';
import { LoaderComponent } from '../../common-ui-components/loader/loader.component';
import { ConfirmationPopupComponent } from '../../common-ui-components/confirmation-popup/confirmation-popup.component';
import { UIIconsComponent } from '../../ui/icons/icons.component';
import { UISkeletonFlatHierarchyComponent } from '../../ui/skeletons/flat-hierarchy/skeleton-flat-hierarchy.component';
import { ScopeOverviewPopupComponent } from '../../common-ui-components/scope-overview-popup/scope-overview-popup.component';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { cloneDeep } from '@apollo/client/utilities';
import { map, throttleTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { isEqual } from 'lodash';
import { SqlDataType } from '../../interfaces/report.model';  

@Component({
  selector: 'app-report-dataselector',
  templateUrl: './report-dataselector.component.html',
  styleUrls: ['./report-dataselector.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UIIconsComponent,
    ConfirmationPopupComponent,
    LoaderComponent,
    UISkeletonFlatHierarchyComponent,
    HighlightPipe,
    CalendarModule,
    FloatLabelModule,
    AgentTriggerComponent,
    ScopeOverviewPopupComponent
  ],
  providers: [DatePipe]
})
export class ReportDataselectorComponent implements OnInit, OnDestroy {
  private assetsApiService = inject(AssetsApiService);
  private datePipe = inject(DatePipe);

  // Input properties from parent
  @Input() loadingDatascopes: boolean = false;
  @Input() templateTypesLoading: boolean = false;
  @Input() isNewState: boolean = false;
  @Input() dateTimeFormat: any[] = [];
  @Input() filterOperators: any[] = [];
    filterJoinOperators: string[] =  ['AND', 'OR'];;
  @Input() filterOperatorKeys: any = {};
  @Input() savedColumnIds: string[] = []; // For saved reports
  @Input() initialSqlFunctionMap: { [columnId: string]: string } = {}; // For saved reports
  @Input() selectedScope: any = null; // For saved reports
  @Input() isSelectedScope: boolean = false; // For saved reports
  @Input() selectedTableIds: string[] = []; // For saved reports
  selectedDateTimeFormat: string = 'custom'; // For saved reports
  @Input() appliedDateTimeFormat: string = ''; // For saved reports
  @Input() appliedFilters: any[] = [];
  @Input() isExecutingReport: boolean = false;
  @Input() isRunDisabled: boolean = false;
  customDateFormat: string = 'dd MMM yyyy'; // For saved reports

  // Output events
  @Output() scopeSelectedEvent = new EventEmitter<any>();
  @Output() applyFiltersEvent = new EventEmitter<any>();
  @Output() runReportEvent = new EventEmitter<{checkedColumns: any, sqlFunctionMap: any, isUserTriggered: boolean}>();
  @Output() cancelReportEvent = new EventEmitter<void>();
  @Output() sqlFunctionAppliedEvent = new EventEmitter<{columnId: string, functionName: string}>();
  @Output() clearSelectionsEvent = new EventEmitter<void>();
  @Output() columnSelectionChangedEvent = new EventEmitter<any[]>();
  // Confirmation popup state
  showConfirmationPopup: boolean = false;
  confirmationPopupMsg: string = '';
  confirmationPopupHeader: string = '';
  confirmationCallbacks: any = { 
    confirmAction: () => this.closeConfirmationPopUp(), 
    cancelAction: () => this.closeConfirmationPopUp() 
  };

  // UI state variables
  tempSelectedScope: any = null;
  isDataPickerCollapsed: boolean = false;
  isSelectScopeOpen: boolean = false;
  isScopeOverViewPopUpOpen: boolean = false;
  searchScopeTerm: string = '';
  searchTerm: string = '';
  scopes: any[] = [];
  filteredScopes: any[] = [];
  allTables: any[] = [];
  selectedTablesForMeasures: any[] = [];
  selectedTablesForAttributes: any[] = [];
  filteredTablesForAttributes: any[] = [];
  filteredTablesForMeasures: any[] = [];
  checkedColumns: any[] = [];
  sqlFunctionMap: { [columnId: string]: string } = {};
  
  // Section expansion state
  isAttributesExpanded: boolean = true;
  allAttributeTablesExpanded: boolean = false;
  isMeasuresExpanded: boolean = false;
  allMeasureTablesExpanded: boolean = false;
  
  // Filter-related state
  isAttributesLoading: boolean = false;
  isFilterDropdownOpen: boolean = false;
  filterTableSelected: string = '';
  filterTableColumns: any[] = [];
  filterColumnSelected: string = '';
  selectedFilterOperator: string = '';
  selectedFilterColumnType: string = '';
  selectedDate: Date | null = null;
  selectedFromDate: Date | null = null;
  selectedToDate: Date | null = null;
  fromValue: string = '';
  toValue: string = '';
  filterValues: string = '';
  selectedFilters: any[] = [];

  isValidSelection: boolean = false;
  
  // Style variables
  sqlFunctionDropDownStyle: any = {};
  private runReportSubject = new Subject<{ checkedColumns: any[], sqlFunctionMap: any, isUserTriggered: boolean }>();
  private destroy$ = new Subject<void>();

  private readonly numericColumnTypes = new Set([
    'int',
    'integer',
    'bigint',
    'smallint',
    'tinyint',
    'mediumint',
    'float',
    'double',
    'double precision',
    'real',
    'decimal',
    'numeric',
    'number'
  ]);

  private readonly dateColumnTypes = new Set([
    'date',
    'datetime',
    'time',
    'timestamp'
  ]);

  private readonly stringColumnTypes = new Set([
    'str',
    'string',
    'varchar',
    'char',
    'character',
    'character varying',
    'text',
    'bool',
    'boolean',
    'json',
    'map',
    'array',
    'row'
  ]);

  constructor() {}

  private normalizeColumnType(type?: string): string {
    if (!type) return '';
    return type.toString().toLowerCase().split('(')[0].trim();
  }

  public isNumericType(type?: string): boolean {
    const normalized = this.normalizeColumnType(type);
    if (!normalized) return false;
    if (this.numericColumnTypes.has(normalized)) {
      return true;
    }
    return /int|decimal|numeric|double|float|real/.test(normalized);
  }

  public isDateOrtTimeType(type: string): boolean {
    const normalized = this.normalizeColumnType(type);
    return this.dateColumnTypes.has(normalized);
  }

  private isStringType(type?: string): boolean {
    const normalized = this.normalizeColumnType(type);
    if (!normalized) return false;
    if (this.stringColumnTypes.has(normalized)) {
      return true;
    }
    return /char|text|string|bool/.test(normalized);
  }

  private normalizeSqlDataType(type?: SqlDataType | string): SqlDataType {
    const normalized = this.normalizeColumnType(type);
    if (!normalized) return 'str';

    if (/int/.test(normalized)) {
      return 'int';
    }

    if (this.numericColumnTypes.has(normalized)) {
      return 'float';
    }

    if (normalized === 'date') return 'date';
    if (normalized === 'datetime') return 'datetime';
    if (normalized === 'time') return 'time';
    if (normalized === 'timestamp') return 'timestamp';

    return 'str';
  }

  public getColumnIcon(type?: string): string {
    const normalized = this.normalizeColumnType(type);

    if (normalized === 'int' || normalized.includes('int')) {
      return 'assets/icons/int.svg';
    }

    if (this.isNumericType(normalized)) {
      // Reuse float icon for remaining numeric types
      return 'assets/icons/float.svg';
    }

    if (normalized === 'date') {
      return 'assets/icons/date.svg';
    }

    if (normalized === 'time') {
      return 'assets/icons/time.svg';
    }

    if (normalized === 'datetime' || normalized === 'timestamp') {
      return 'assets/icons/datetime.svg';
    }

    return 'assets/icons/str.svg';
  }

  ngOnInit(): void {
    this.runReportSubject
      .pipe(
        throttleTime(700),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        this.runReportEvent.emit(data);
      });

    // Initialize with saved data if available
    this.savedColumnIds = Array.isArray(this.savedColumnIds) ? this.savedColumnIds : [];
    
    if (this.initialSqlFunctionMap && Object.keys(this.initialSqlFunctionMap).length > 0) {
      this.sqlFunctionMap = { ...this.initialSqlFunctionMap };
    }
    
    if (this.appliedFilters?.length > 0) {
      this.selectedFilters = [...this.appliedFilters];
    }
        
    // Initialize default date format if not set
    if (this.dateTimeFormat?.length > 0 && !this.appliedDateTimeFormat) {
      this.appliedDateTimeFormat = this.dateTimeFormat[0]?.value || '';
    }
    
    // Handle saved report initialization
    if (this.selectedScope?.id) {
      this.isSelectedScope = true;
      
      if (this.savedColumnIds?.length > 0) {
        this.isNewState = true;
        this.getDatascopes(true);
      } else {
        this.fetchTablesForScope(this.selectedScope);
      }
    } else {
      this.getDatascopes(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Methods for confirmation popup
  showConfirmation(message: string, header: string, confirmAction?: () => void): void {
    this.confirmationPopupMsg = message;
    this.confirmationPopupHeader = header || 'Confirmation';
    this.confirmationCallbacks.confirmAction = confirmAction 
      ? () => { confirmAction(); this.closeConfirmationPopUp(); }
      : () => this.closeConfirmationPopUp();
    
    this.showConfirmationPopup = true;
  }
  
  closeConfirmationPopUp(): void {
    this.showConfirmationPopup = false;
    this.confirmationPopupMsg = '';
    this.confirmationPopupHeader = '';
    
    // Reset callbacks to defaults
    this.confirmationCallbacks = {
      confirmAction: () => this.closeConfirmationPopUp(),
      cancelAction: () => this.closeConfirmationPopUp()
    };
  }

  // Method to initialize tables for saved reports
  public initializeTablesForSavedReport(): void {
    if (!this.selectedScope) {
      console.error("Cannot initialize tables: No scope selected");
      return;
    }
    
    // Initialize settings from inputs
    if (Object.keys(this.initialSqlFunctionMap).length > 0) {
      this.sqlFunctionMap = { ...this.initialSqlFunctionMap };
    }
    
    if (this.selectedDateTimeFormat) {
      // Set default custom format if needed
      if (this.selectedDateTimeFormat === 'custom' && !this.customDateFormat) {
        this.customDateFormat = 'dd MMM yyyy';
      }
    }
    
    if (this.selectedFilters.length > 0) {
      this.appliedFilters = [...this.selectedFilters];
    }

    this.isSelectedScope = true;
    
    // Initialize columns from saved state
    if (this.savedColumnIds?.length > 0) {
      this.checkedColumns = this.savedColumnIds.map(id => ({
        id,
        checked: true,
      }));
    }
    
    // Load tables and their columns
    this.fetchTablesForScope(this.selectedScope);
    
    // Enable report execution
    this.isValidSelection = true;
    this.isNewState = true;
    
  }

  // UI state toggle methods
  toggleCollapse( toggleValue?: boolean): void {
    this.isDataPickerCollapsed =  toggleValue ?? !this.isDataPickerCollapsed;
    
    if (!this.isDataPickerCollapsed && this.savedColumnIds.length > 0) {
      setTimeout(() => this.updateSelectionFlags(), 100);
    }
  }

  toggleSelectScope(): void {
    if (!this.isSelectScopeOpen && this.scopes.length === 0) {
      this.getDatascopes();
    }
    this.isSelectScopeOpen = !this.isSelectScopeOpen;
    this.tempSelectedScope = this.selectedScope;
  }

  toggleScopeOverView(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isScopeOverViewPopUpOpen = !this.isScopeOverViewPopUpOpen;
  }

  // Methods for scope selection
  selectScope(scope: any, event: any): void {
    this.tempSelectedScope = scope;
    this.selectedScope = scope;
    this.isSelectedScope = true;
    this.isSelectScopeOpen = false;
    
    // Always clear selections when switching scopes
    this.resetSelections();
    
    this.fetchTablesForScope(scope);
    this.scopeSelectedEvent.emit(scope);
    event.stopPropagation();
  }
  
  // Helper to reset selections
  private resetSelections(): void {
    this.checkedColumns = [];
    this.sqlFunctionMap = {};
    this.selectedFilters = [];
    this.appliedFilters = [];
    this.savedColumnIds = [];
    this.selectedTableIds = [];
    this.isValidSelection = false;
  }
  
  // For direct scope selection in code
  public programmaticSelectScope(scope: any): void {
    if (!scope?.id) return;
    
    this.tempSelectedScope = scope;
    this.selectedScope = scope;
    this.isSelectedScope = true;
    this.isSelectScopeOpen = false;
    
    this.fetchTablesForScope(scope);
  }

  private fetchTablesForScope(scope: any): void {
    if (!scope?.id) {
      console.error('Invalid scope provided to fetchTablesForScope');
      return;
    }

    this.isSelectedScope = true;
    
    this.getAssets([scope.id], ['TABLE']).subscribe({
      next: (tables: any) => {
        this.processTablesData(tables);

        if (this.savedColumnIds?.length > 0) {
          this.processColumnsForSavedReport();
          
          // Execute the report after columns are set up
          setTimeout(() => this.executeReportData(), 300);
        }
      },
      error: (error: any) => {
        console.error('Error fetching tables for scope:', error);
      }
    });
  }
  
  private processTablesData(tables: any[]): void {
    // Initialize tables with needed properties
    this.allTables = tables.map((table: any) => ({
      ...table,
      expanded: false,
      checked: false,
      loading: false,
      columns: []
    }));
    
    // Reset selections for new reports only
    if (this.savedColumnIds.length === 0) {
      this.checkedColumns = [];
      this.sqlFunctionMap = {};
    }
    
    // Initialize or validate table IDs
    if (this.selectedTableIds.length === 0) {
      this.selectedTableIds = this.allTables.map(table => table.id);
    } else {
      // For saved reports, ensure table IDs are valid
      this.selectedTableIds = this.selectedTableIds.filter(
        id => this.allTables.some(table => table.id === id)
      );
      
      if (this.selectedTableIds.length === 0) {
        this.selectedTableIds = this.allTables.map(table => table.id);
      }
    }

    // Update tables for measures and attributes
    this.updateSelectedTables('MEASURES');
    this.updateSelectedTables('ATTRIBUTES');
    
    // For saved reports, expand relevant tables and load their columns
    if (this.savedColumnIds.length > 0) {
      const tablesToExpand = this.selectedTableIds.length > 0 ? 
        this.selectedTableIds : 
        this.allTables.map(table => table.id);
      
      this.expandSavedReportTables(tablesToExpand);
    }
  }
  
  private expandSavedReportTables(tableIds: string[]): void {
    tableIds.forEach(tableId => {
      const attributeTable = this.selectedTablesForAttributes.find(t => t.id === tableId);
      const measureTable = this.selectedTablesForMeasures.find(t => t.id === tableId);
      
      if (attributeTable) {
        attributeTable.expanded = true;
        this.getColumnForTable(attributeTable, tableId);
      }
      
      if (measureTable) {
        measureTable.expanded = true;
        this.getColumnForTable(measureTable, tableId);
      }
    });
    
    // Expand sections to show tables
    this.isAttributesExpanded = true;
    this.isMeasuresExpanded = true;
    
    // Update validation status after loading
    setTimeout(() => this.updateSelectionFlags(), 500);
  }

  getAssets(primaryAssetId: string[], secondaryAssetType: string[]): any {
    return this.assetsApiService
      .findAssetsByRelationship(primaryAssetId, secondaryAssetType)
      .pipe(map((response: any) => response.data.findAssetsByRelationship));
  }

  filterScopes(): void {
    if (!this.scopes.length) return;
    
    const term = this.searchScopeTerm.toLowerCase();
    this.filteredScopes = term ? 
      this.scopes.filter(scope => scope.name.toLowerCase().includes(term)) : 
      [...this.scopes];
  }

  public getDatascopes(isSavedReport = false): void {
    this.loadingDatascopes = true;
    this.assetsApiService
      .getAssetByType('DATASCOPE', 'network-only')
      .subscribe({
        next: (response) => {
          this.scopes = this.filteredScopes = response.data.assetsByType.map((model: any) => ({
            id: model.id,
            name: model.name,
            connectionId: model?.parameters?.CONNECTIONS?.[0]?.id,
          }));
          this.loadingDatascopes = false;
          
          // For saved reports, initialize with the saved scope
          if (isSavedReport && this.selectedScope?.id) {
            const foundScope = this.scopes.find(scope => scope.id === this.selectedScope.id);
            
            if (foundScope) {
              this.tempSelectedScope = this.selectedScope = foundScope;
              this.initializeTablesForSavedReport();
              
              // Auto-execute the report if needed
              if (this.savedColumnIds?.length > 0) {
                setTimeout(() => this.executeReportData(), 800);
              }
            } else {
              console.error('Selected scope not found in loaded scopes');
            }
          }
        },
        error: (error) => {
          console.error('Error loading data scopes:', error);
          this.loadingDatascopes = false;
        }
      });
  }

  onSearch(): void {
    const term = this.searchTerm.trim().toLowerCase();
  
    // Reset if empty search
    if (!term) {
      this.filteredTablesForMeasures = [...this.selectedTablesForMeasures];
      this.filteredTablesForAttributes = [...this.selectedTablesForAttributes];
      return;
    }
    
    // Filter measures tables and columns
    this.filteredTablesForMeasures = this.filterTablesBySearchTerm(
      this.selectedTablesForMeasures, 
      term, 
      (col: any) => this.isNumericType(col.parameters?.TYPE)
    );
    
    // Filter attributes tables and columns
    this.filteredTablesForAttributes = this.filterTablesBySearchTerm(
      this.selectedTablesForAttributes, 
      term, 
      (col: any) => !this.isNumericType(col.parameters?.TYPE)
    );
    
    // Expand sections to show search results
    this.isMeasuresExpanded = true;
    this.isAttributesExpanded = true;
  }
  
  private filterTablesBySearchTerm(tables: any[], term: string, columnFilter: (col: any) => boolean): any[] {
    return tables
      .map((table: any) => {
        // Check if table name matches
        const tableNameMatches = table.name.toLowerCase().includes(term);
        
        // Find matching columns
        const filteredColumns = table.columns?.filter((column: any) =>
          column.name.toLowerCase().includes(term) && columnFilter(column)
        ) || [];
  
        if (tableNameMatches) {
          return { ...table, expanded: true };
        } else if (filteredColumns.length) {
          return { ...table, columns: filteredColumns, expanded: true };
        }
        return null;
      })
      .filter(Boolean); // Remove null entries
  }

  clearAllClicked(): void {
    this.showConfirmation(
      'All selections will be cleared. Are you sure you want to continue?',
      'Clear Selections',
      () => {
        this.checkedColumns = [];
        this.sqlFunctionMap = {};
        this.appliedFilters = [];
        this.clearAllTableCheckboxes();
        this.updateSelectionFlags();
        this.clearAllFilters();

        this.clearSelectionsEvent.emit();
      }
    );
  }
  
  private clearAllTableCheckboxes(): void {
    const clearCheckboxes = (tables: any[]) => {
      tables.forEach(table => {
        if (table.columns?.length) {
          table.columns.forEach((column: any) => column.checked = false);
        }
      });
    };
    
    clearCheckboxes(this.selectedTablesForMeasures);
    clearCheckboxes(this.selectedTablesForAttributes);
  }

  updateSelectedTables(type: string): void {
    const tables = this.allTables.map((value: any) => ({
      ...value,
      template: type,
      columns: value.columns || []
    }));
    
    if (type === 'MEASURES') {
      this.selectedTablesForMeasures = tables;
      this.filteredTablesForMeasures = [...tables];
      tables.forEach(table => this.getColumnForTable(table, table.id));
    } else if (type === 'ATTRIBUTES') {
      this.isAttributesLoading = true;
      this.selectedTablesForAttributes = tables;
      this.filteredTablesForAttributes = [...tables];
      tables.forEach(table => this.getColumnForTable(table, table.id));
    }
  }

  private getColumnForTable(table: any, tableId: string): void {
    table.loading = true;
    this.getAssets([this.selectedScope.id, tableId], ['COLUMN']).subscribe({
      next: (columns: any) => {
        table.columns = this.processColumns(columns, tableId);
        
        // For saved reports, expand tables with selected columns
        if (this.savedColumnIds?.length > 0) {
          const savedColumnSet = new Set(this.savedColumnIds);
          const hasSelectedColumns = table.columns.some((col: any) => savedColumnSet.has(col.id));
          table.expanded = hasSelectedColumns || table.expanded || false;
        } else {
          table.expanded = table.expanded || false;
        }
        
        table.loading = false;
        this.checkIfAllAttributesLoaded();
      },
      error: () => {
        table.loading = false;
        this.checkIfAllAttributesLoaded();
      }
    });
  }
  
  private processColumns(columns: any[], tableId: string): any[] {
    const savedColumnSet = new Set(this.savedColumnIds);
    
    return columns.map((column: any) => {
      const isAlreadySaved = savedColumnSet.has(column.id);
      const isAlreadyChecked = this.isColumnChecked(column.id);
      const hasSqlFunction = !!this.sqlFunctionMap[column.id];
      
      // Determine if column should be checked
      const checked = isAlreadySaved || isAlreadyChecked || hasSqlFunction;
      
      return { 
        ...column, 
        tableId,
        checked 
      };
    });
  }
  
  private updateCheckedColumnsArray(column: any, tableId: string): void {
    const existingColumn = this.checkedColumns.find(c => c.id === column.id);
    
    if (existingColumn) {
      // Update existing entry with missing info
      existingColumn.tableId = existingColumn.tableId || tableId;
      existingColumn.parameters = existingColumn.parameters || column.parameters || {};
    } else {
      // Add new entry
      this.checkedColumns.push({
        id: column.id,
        tableId,
        checked: true,
        parameters: column.parameters || {}
      });
    }
  }
  
  private isColumnChecked(columnId: string): boolean {
    return this.checkedColumns.some(c => c.id === columnId);
  }
  
  private checkIfAllAttributesLoaded(): void {
    this.isAttributesLoading = this.selectedTablesForAttributes.some(table => table.loading === true);
  }

  toggleFilterDropdown(event: any): void {
    this.isFilterDropdownOpen = !this.isFilterDropdownOpen;
    event?.stopPropagation();
  }

  confirmFilterPopUpClose(): void {
    // Check if there are unsaved changes
    const hasFilterChanges = this.selectedFilters.length > 0 && 
                            !isEqual(this.selectedFilters, this.appliedFilters);
    const hasPartialNewFilter = this.filterTableSelected || 
                               this.filterColumnSelected || 
                               this.selectedFilterOperator || 
                               this.filterValues;
    const hasDateFormatChanges = this.selectedDateTimeFormat && 
                                this.appliedDateTimeFormat !== this.selectedDateTimeFormat;
    
    if (hasFilterChanges || hasPartialNewFilter || hasDateFormatChanges) {
      this.showConfirmation(
        'Unapplied filters will be lost. Do you want to discard them and proceed?',
        'Confirm Close',
        () => {
          this.isFilterDropdownOpen = false;
          this.selectedFilters = cloneDeep(this.appliedFilters);
          this.clearAddFilterValues();
        }
      );
    } else {
      this.isFilterDropdownOpen = false;
    }
  }

  // Filter-related methods
  onFilterTableSelected(event: any): void {
    this.filterTableSelected = event;
    
    // Find the selected table
    const selectedTable = this.allTables.find(table => table.id === event);
    this.filterTableColumns = [];
    
    if (!selectedTable) return;
    
    // Load columns if needed
    if (!selectedTable.columns || selectedTable.columns.length === 0) {
      this.getAssets([this.selectedScope.id, event], ['COLUMN']).subscribe({
        next: (columns: any) => {
          selectedTable.columns = this.processColumns(columns, event);
          this.filterTableColumns = selectedTable.columns || [];
        },
        error: (error: any) => {
          console.error('Error loading columns for filter:', error);
        }
      });
    } else {
      this.filterTableColumns = selectedTable.columns || [];
    }
  }

  onFilterColumnChanges(event: any): void {
    this.filterColumnSelected = event;
    const selectedColumn = this.filterTableColumns.find(col => col.parameters?.FQPN === event);
    this.selectedFilterColumnType = selectedColumn?.parameters?.TYPE || '';
  }

  clearNewFilterValue(): void {
    this.selectedDate = this.selectedFromDate = this.selectedToDate = null;
    this.fromValue = this.toValue = this.filterValues = '';
  }

  onFilterInputKeyPress(event: any, type: string, isMultiple: boolean = false): void {
    // Only allow numbers and decimal point for numeric fields
    if (this.isNumericType(type)) {
      const charCode = event.which || event.keyCode;
      const isNumber = charCode >= 48 && charCode <= 57; // 0-9
      const isDecimalPoint = charCode === 46; // .
      const isControlKey = charCode <= 31; // Control keys
      
      if (!isNumber && !isDecimalPoint && !isControlKey) {
        event.preventDefault();
      }
    }
  }

  addFilter(): void {
    const selectedTable = this.allTables.find((table)=> table.id ===  this.filterTableSelected);
    const selectedColumn = this.filterTableColumns?.find(column => column.parameters.FQPN === this.filterColumnSelected);
    // Create a new filter object
    const newFilter: any = {
      table_id: this.filterTableSelected,
      table_name: selectedTable?.name ?? '',
      TABLE_FQPN: selectedTable?.parameters?.FQPN ?? '',
      COLUMN_FQPN: this.filterColumnSelected,
      COLUMN_TYPE: this.selectedFilterColumnType,
      operator: this.selectedFilterOperator,
      isEditing: false,
      column_id: selectedColumn?.id,
      column_name: selectedColumn?.name || '',
      // Add join if not first filter
      join: '',
    };

    // Set values based on type and operator
    const isBetweenOperator = (
      this.selectedFilterOperator === this.filterOperatorKeys.BETWEEN || 
      this.selectedFilterOperator === this.filterOperatorKeys.NOT_BETWEEN
    );
    
    if (this.isDateOrtTimeType(this.selectedFilterColumnType)) {
      if (isBetweenOperator) {
        newFilter.fromDate = this.selectedFromDate;
        newFilter.toDate = this.selectedToDate;
      } else {
        newFilter.fromDate = this.selectedDate;
      }
    } else {
      if (isBetweenOperator) {
        newFilter.fromValue = this.fromValue;
        newFilter.toValue = this.toValue;
      } else {
        newFilter.values = this.filterValues;
      }
    }

    if(this.selectedFilters.length > 0){
      this.selectedFilters[this.selectedFilters.length-1].join = 'AND';
    }

    this.selectedFilters.push(newFilter);
    this.clearAddFilterValues();
  }

  clearAddFilterValues(): void {
    this.filterTableSelected = '';
    this.filterColumnSelected = '';
    this.selectedFilterOperator = '';
    this.selectedFilterColumnType = '';
    this.clearNewFilterValue();
    this.filterTableColumns = [];
  }

  clearAllFilters(): void {
    this.selectedFilters = [];
  }

  onDateFormatChange(event: any): void {
    this.selectedDateTimeFormat = event;
    
    // Initialize with default format if custom is selected
    if (event === 'custom' && !this.customDateFormat) {
      this.customDateFormat = 'dd MMM yyyy';
    }
    
    // Set default applied format if not already set
    if (this.dateTimeFormat?.length > 0 && !this.appliedDateTimeFormat) {
      this.appliedDateTimeFormat = this.dateTimeFormat[0]?.value || '';
    }
  }

  onCustomFormatChange(event: any): void {
    this.customDateFormat = event;
  }

  getFormattedDate(format: string): string {
    return this.datePipe.transform(
      new Date(), 
      format === 'custom' ? this.customDateFormat : format
    ) || '';
  }

  editFilter(filter: any, index?: number): void {
    filter.isEditing = true;
  }

  removeFilter(index: number): void {
    this.selectedFilters.splice(index, 1);
  }

  onFilterTableEdited(filter: any, event: any): void {
    filter.table_id = event;
    
    // Find the selected table
    const selectedTable = this.allTables.find(table => table.id === event);
    filter.tableColumns = [];
    
    if (!selectedTable) return;
    
    // Load columns if needed
    if (!selectedTable.columns || selectedTable.columns.length === 0) {
      this.getAssets([this.selectedScope.id, event], ['COLUMN']).subscribe({
        next: (columns: any) => {
          selectedTable.columns = this.processColumns(columns, event);
          filter.tableColumns = selectedTable.columns || [];
        },
        error: (error: any) => {
          console.error('Error loading columns for edited filter:', error);
        }
      });
    } else {
      filter.tableColumns = selectedTable.columns || [];
    }
  }

  onFilterColumnEdited(event: any, filter: any): void {
    filter.COLUMN_FQPN = event.target.value;
    
    // Update column type
    const selectedTable = this.allTables.find(table => table.id === filter.table_id);
    if (selectedTable?.columns) {
      const column = selectedTable.columns.find(
        (col: any) => col.parameters?.FQPN === filter.COLUMN_FQPN
      );
      filter.COLUMN_TYPE = column?.parameters?.TYPE || '';
    }
  }

  clearFilterValues(filter: any): void {
    filter.fromDate = filter.toDate = null;
    filter.fromValue = filter.toValue = filter.values = '';
  }

  updateFilter(filter: any, index?: number): void {
    filter.isEditing = false;
  }

  clearEditedFilter(filter: any, index?: number): void {
    filter.isEditing = false;
  }

  onJoinEdited(filter: any, event: any, index?: number): void {
    filter.join = event.target.value;
  }

  applyFilters(): void {
    // Store current date format as applied format
    this.appliedDateTimeFormat = this.selectedDateTimeFormat;
    
    // Update and close filters
    this.isFilterDropdownOpen = false;
    this.appliedFilters = cloneDeep(this.selectedFilters);
    this.applyFiltersEvent.emit(this.selectedFilters);
  }

  // Section toggle methods
  toggleAttributes(): void {
    this.isAttributesExpanded = !this.isAttributesExpanded;
    if (!this.isAttributesExpanded && this.selectedTablesForAttributes.length === 0) {
      this.updateSelectedTables('ATTRIBUTES');
    }
  }

  toggleMeasures(): void {
    this.isMeasuresExpanded = !this.isMeasuresExpanded;
    if (!this.isMeasuresExpanded && this.selectedTablesForMeasures.length === 0) {
      this.updateSelectedTables('MEASURES');
    }
  }

  expandAllTables(sectionType: string): void {
    const isAttributes = sectionType === 'ATTRIBUTES';
    
    // Ensure section is expanded
    if (isAttributes) {
      this.isAttributesExpanded = true;
      this.allAttributeTablesExpanded = !this.allAttributeTablesExpanded;
      this.expandOrCollapseTables(this.filteredTablesForAttributes, this.allAttributeTablesExpanded);
    } else {
      this.isMeasuresExpanded = true;
      this.allMeasureTablesExpanded = !this.allMeasureTablesExpanded;
      this.expandOrCollapseTables(this.filteredTablesForMeasures, this.allMeasureTablesExpanded);
    }
  }
  
  private expandOrCollapseTables(tables: any[], shouldExpand: boolean): void {
    tables.forEach(table => {
      table.expanded = shouldExpand;
      if (table.expanded && (!table.columns || table.columns.length === 0)) {
        this.getColumnForTable(table, table.id);
      }
    });
  }

  toggleTable(tableId: string, sectionType: string): void {
    const tables = sectionType === 'ATTRIBUTES' ? 
      this.filteredTablesForAttributes : 
      this.filteredTablesForMeasures;
    
    const table = tables.find(t => t.id === tableId);
    if (!table) return;
    
    table.expanded = !table.expanded;
    
    // Load columns if expanding and no columns loaded yet
    if (table.expanded && (!table.columns || table.columns.length === 0)) {
      this.getColumnForTable(table, tableId);
    }
  }

  // Column selection and SQL function methods
  onColumnCheckedChange(column: any, tableId: string): void {
    // Create a full copy including parameters
    const columnWithTableId = { 
      ...column, 
      tableId,
      parameters: { ...column.parameters } 
    };
  
    if (column.checked) {
      // Add to checked columns if not already present
      if (!this.checkedColumns.some(c => c.id === column.id)) {
        this.checkedColumns.push(columnWithTableId);
      }
    } else {
      // Remove from checked columns
      this.checkedColumns = this.checkedColumns.filter(c => c.id !== column.id);
      // Remove any SQL function applied to this column
      delete this.sqlFunctionMap[column.id];
    }

    this.emitColumnSelectionChanged();
    this.updateSelectionFlags();
  }

  public openSqlFunctionsDropDown(
    column: any,
    event: MouseEvent,
    isAttribute = false
  ) {
    this.closeAllSqlFunctionDropdowns(column.id);
    column.isSQLFunctionDropdownOpen = !column.isSQLFunctionDropdownOpen;
    event.stopPropagation();
  
    if (!column.isSQLFunctionDropdownOpen) return;
  
    const { top: t, bottom: b, left: l } =
      (event.target as HTMLElement).getBoundingClientRect();
  
    const scrollY = window.scrollY || 0;
    const scrollX = window.scrollX || 0;
    
    const top    = isAttribute ? b + scrollY : t + scrollY;
    const left   = l + scrollX;
    const transform = isAttribute ? 'none' : 'translateY(-100%)';
  
    this.sqlFunctionDropDownStyle = {
      position: 'fixed',
      top:       `${top}px`,
      left:      `${left}px`,
      transform,           // flip upward for measures
    };
  }  
  
  private closeAllSqlFunctionDropdowns(exceptColumnId?: string): void {
    const processTable = (table: any) => {
      table.columns?.forEach((col: any) => {
        if (!exceptColumnId || col.id !== exceptColumnId) {
          col.isSQLFunctionDropdownOpen = false;
        }
      });
    };
    
    this.filteredTablesForAttributes.forEach(processTable);
    this.filteredTablesForMeasures.forEach(processTable);
  }

  applySQLFunctionToSelectedColumn(column: any, tableId: string, functionName: string): void {
    if (this.sqlFunctionMap[column.id] === functionName) {
      // Toggle off if same function selected
      delete this.sqlFunctionMap[column.id];
    } else {
      // Apply new function
      this.sqlFunctionMap[column.id] = functionName;
    }
    
    column.isSQLFunctionDropdownOpen = false;
    
    // Notify parent
    this.sqlFunctionAppliedEvent.emit({
      columnId: column.id,
      functionName: this.sqlFunctionMap[column.id] || ''
    });
    
    this.updateSelectionFlags();
  }

  executeReportData(isUserTriggered: boolean = false): void {
    // Get table IDs - use checked columns first, fall back to selected or all tables
    const tableIds = this.getTableIdsForReport();

    // Get column IDs from checked columns or saved columns
    const columnIds = this.checkedColumns.length > 0
      ? this.checkedColumns.map(column => column.id)
      : this.savedColumnIds;

    // Force new state to true when executing report
    this.isNewState = true;

    this.runReportSubject.next({
      checkedColumns: cloneDeep(this.checkedColumns),
      sqlFunctionMap: { ...this.sqlFunctionMap },
      isUserTriggered: isUserTriggered
    });
  }
  
  private getTableIdsForReport(): string[] {
    // First try to get tableIds from checkedColumns
    if (this.checkedColumns.length > 0) {
      const uniqueTableIds = new Set(
        this.checkedColumns
          .map(column => column.tableId)
          .filter(Boolean)
      );
      
      if (uniqueTableIds.size > 0) {
        return Array.from(uniqueTableIds);
      }
    }
    
    // Fall back to selectedTableIds
    if (this.selectedTableIds.length > 0) {
      return [...this.selectedTableIds];
    }
    
    // Last resort: use all tables
    return this.allTables.map(table => table.id);
  }

  // Utility methods
  isAddFilterDisabled(): boolean {
    if (!this.filterTableSelected || !this.filterColumnSelected || !this.selectedFilterOperator) {
      return true;
    }

    const isBetweenOperator = (
      this.selectedFilterOperator === this.filterOperatorKeys.BETWEEN ||
      this.selectedFilterOperator === this.filterOperatorKeys.NOT_BETWEEN
    );

    if (this.isDateOrtTimeType(this.selectedFilterColumnType)) {
      return isBetweenOperator 
        ? !this.selectedFromDate || !this.selectedToDate
        : !this.selectedDate;
    } else {
      return isBetweenOperator
        ? !this.fromValue || !this.toValue
        : !this.filterValues;
    }
  }

  isUpdateButtonDisabled(filter: any): boolean {
    if (!filter.table_id || !filter.COLUMN_FQPN || !filter.operator) {
      return true;
    }

    const isBetweenOperator = (
      filter.operator === this.filterOperatorKeys.BETWEEN ||
      filter.operator === this.filterOperatorKeys.NOT_BETWEEN
    );

    if (this.isDateOrtTimeType(filter.COLUMN_TYPE)) {
      return isBetweenOperator
        ? !filter.fromDate || !filter.toDate
        : !filter.fromDate;
    } else {
      return isBetweenOperator
        ? !filter.fromValue || !filter.toValue
        : !filter.values;
    }
  }

  getOperatorValue(operatorKey: string): string {
    const operator = this.filterOperators.find(op => op.key === operatorKey);
    return operator?.label || operatorKey;
  }

  isTableColumnChecked(table: any, isMeasure: boolean): number {
    if (!table.columns?.length) return 0;
    
    return table.columns.filter((col: any) => {
      const isNumericType = this.isNumericType(col.parameters?.TYPE);
      return col.checked && (isMeasure ? isNumericType : !isNumericType);
    }).length;
  }

  public getSqlFunctions(dataType: SqlDataType | string): string[] {
  const numericFns   = ['COUNT','COUNT_DISTINCT','SUM','AVG','MIN','MAX','ABS','CEIL','FLOOR','ROUND'];
  const countOnly    = ['DISTINCT','COUNT', 'COUNT_DISTINCT'];
  const minMaxOnly   = [...countOnly,'MIN','MAX'];
  const normalizedType = this.normalizeSqlDataType(dataType);

  switch (normalizedType) {
    case 'int':
    case 'float':
      return numericFns;

    case 'str':
      return countOnly;

    case 'date':
    case 'datetime':
    case 'time':
    case 'timestamp':
      return minMaxOnly;

    default:
      return countOnly;
  }
}

  checkIfFunctionApplied(columnId: string, functionName: string): boolean {
    return this.sqlFunctionMap[columnId] === functionName;
  }

  isCustomFormatValid(): boolean {
    return !(this.selectedDateTimeFormat === 'custom' && 
             (!this.customDateFormat || this.customDateFormat.trim() === ''));
  }

  currentDate(): Date {
    return new Date();
  }

  closeFunctionsDropDown(): void {
    this.closeAllSqlFunctionDropdowns();
  }

  cancelReport(): void {
    this.cancelReportEvent.emit();
  }
  
  // Update validation state based on current selections
  public updateSelectionFlags(): void {
    if (this.checkedColumns.length === 0) {
      this.isValidSelection = false;
      return;
    }

    // Check if we have at least one non-numeric column or a numeric column with a SQL function
    const hasValidColumnSelection = this.checkedColumns.some((column: any) => {
      const isNumeric = this.isNumericType(column.parameters?.TYPE);
      return !isNumeric || this.sqlFunctionMap[column.id];
    });
    
    const allNumericColumnsHaveFunctions = !this.checkedColumns.some((column: any) => {
      const isNumeric = this.isNumericType(column.parameters?.TYPE);
      return isNumeric && !this.sqlFunctionMap[column.id];
    });
    
    this.isValidSelection = hasValidColumnSelection && allNumericColumnsHaveFunctions;
    
    // Mark new state if valid selection
    if (this.isValidSelection) {
      this.isNewState = true;
    }
  }

  // Update UI after loading data from saved report
  public syncWithShellState(state: any): void {
    if (!state) return;
    
    // Sync data from shell state
    if (state.sqlFunctionMap) {
      this.sqlFunctionMap = { ...state.sqlFunctionMap };
    }
    
    if (state.selectedDateTimeFormat) {
      this.selectedDateTimeFormat = state.selectedDateTimeFormat;
    }
    
    if (state.appliedDateTimeFormat) {
      this.appliedDateTimeFormat = state.appliedDateTimeFormat;
    }
    
    if (state.customDateFormat) {
      this.customDateFormat = state.customDateFormat;
    }
    
    if (state.filters?.length > 0) {
      this.selectedFilters = this.appliedFilters = [...state.filters];
    }
    
    if (state.columnIds?.length > 0) {
      this.savedColumnIds = [...state.columnIds];
    }
    
    if (state.tableIds?.length > 0) {
      this.selectedTableIds = [...state.tableIds];
    }    
    // For saved reports with report data
    if (state.reportVisualData?.length > 0) {
      this.isValidSelection = true;
    }
    
    // Expand sections for saved reports
    if (this.savedColumnIds?.length > 0) {
      this.isAttributesExpanded = this.isMeasuresExpanded = true;
      this.expandTablesWithSavedColumns();
    }
    
    // Force isSelectedScope to true for saved reports
    this.isSelectedScope = true;
    
    // Update UI and validation
    setTimeout(() => {
      this.updateColumnCheckboxes();
      this.updateSelectionFlags();
    }, 100);
  }
  
  private expandTablesWithSavedColumns(): void {
    if (!this.savedColumnIds?.length) return;
    
    const savedColumnSet = new Set(this.savedColumnIds);
    const sqlFunctionKeys = Object.keys(this.sqlFunctionMap);
    
    this.allTables.forEach(table => {
      if (!table.columns?.length) return;
      
      // Check if table contains saved columns
      const hasSelectedColumns = table.columns.some((column: any) => 
        savedColumnSet.has(column.id) || sqlFunctionKeys.includes(column.id)
      );
      
      if (hasSelectedColumns) {
        table.expanded = true;
        this.updateTableColumnSelections(table, savedColumnSet);
      }
    });
  }
  
  private updateTableColumnSelections(table: any, savedColumnSet: Set<string>): void {
    table.columns.forEach((column: any) => {
      if (savedColumnSet.has(column.id) || this.sqlFunctionMap[column.id]) {
        column.checked = true;
        
        // Make sure it's in checkedColumns
        if (!this.checkedColumns.some(c => c.id === column.id)) {
          this.checkedColumns.push({
            id: column.id,
            tableId: table.id,
            checked: true,
            parameters: column.parameters || {}
          });
        }
      }
    });
  }
  
  // Update all checkboxes to match checked columns
  private updateColumnCheckboxes(): void {
    if (!this.checkedColumns.length) return;
    
    const checkedColumnIds = new Set(this.checkedColumns.map(col => col.id));
    const updateTableColumns = (table: any) => {
      if (!table.columns?.length) return;
      
      table.columns.forEach((column: any) => {
        // Column should be checked if it's in checkedColumns or has a SQL function
        column.checked = checkedColumnIds.has(column.id) || !!this.sqlFunctionMap[column.id];
        
        // Update checked columns collection
        if (column.checked) {
          this.updateCheckedColumnsArray(column, table.id);
        }
      });
    };
    
    // Apply to both tables collections
    this.selectedTablesForMeasures.forEach(updateTableColumns);
    this.selectedTablesForAttributes.forEach(updateTableColumns);
    
    // Expand sections if needed
    this.expandSectionsWithSelectedColumns();
  }
  
  private expandSectionsWithSelectedColumns(): void {
    if (this.checkedColumns.length === 0) return;
    
    // Check if we have measures or attributes selected
    const hasMeasures = this.selectedTablesForMeasures.some(table => 
      table.columns?.some((col: any) => col.checked)
    );
    
    const hasAttributes = this.selectedTablesForAttributes.some(table => 
      table.columns?.some((col: any) => col.checked)
    );
    
    // Expand relevant sections
    if (hasMeasures) this.isMeasuresExpanded = true;
    if (hasAttributes) this.isAttributesExpanded = true;
  }

  getColumnsForEditedTable(tableId: string): any[] {
    const table = this.allTables.find(t => t.id === tableId);
    if (table && (!table.columns || table.columns.length === 0)) {
      // Return empty array now, but load columns asynchronously
      this.getAssets([this.selectedScope.id, tableId], ['COLUMN']).subscribe({
        next: (columns: any) => {
          table.columns = this.processColumns(columns, tableId);
          // Force UI update if a filter is currently being edited with this table
          this.selectedFilters.forEach(filter => {
            if (filter.isEditing && filter.table_id === tableId) {
              filter.tableColumns = table.columns || [];
            }
          });
        },
        error: (error: any) => {
          console.error('Error loading columns for edited table:', error);
        }
      });
      return [];
    }
    return table ? table.columns || [] : [];
  }

  public getSQLFunctionForColumn(columnId: string): string {
    return this.sqlFunctionMap[columnId] || '';
  }

  private emitColumnSelectionChanged(): void {  
    this.columnSelectionChangedEvent.emit(this.checkedColumns);
  }

  public get showDateTimeFormatPicker(): boolean {
    // Return true if there is at least one filter with a date
    return this.selectedFilters.some((filter) =>(filter.fromDate ||  filter.toDate));
  }

  private processColumnsForSavedReport(): void {
    this.checkedColumns = this.savedColumnIds.map((id) => {
      // Now we can find tableId because allTables is populated
      const column = this.allTables.flatMap(table => table.columns).find(col => col.id === id);
      return { 
        id, 
        tableId: column?.tableId, 
        checked: true,
        parameters: column?.parameters || {} 
      };
    });
    
    // Expand tables that have saved columns
    if (this.selectedTableIds.length > 0) {
      this.expandSavedReportTables(this.selectedTableIds);
    } else {
      // If no tableIds, expand any table that might contain the selected columns
      const tablesWithSelectedColumns = this.allTables.filter(table => 
        table.columns.some((col: any) => this.savedColumnIds.includes(col.id))
      );
      
      this.expandSavedReportTables(tablesWithSelectedColumns.map(t => t.id));
    }
    if (this.savedColumnIds?.length > 0) {
      setTimeout(() => this.executeReportData(), 500);
    }
  }
} 
